/**
 * Reglas puras de presentación de la tarjeta "Tu jornada" (006 · T024; research R-5,
 * data-model.md §4.2).
 *
 * `journeyCardOf` **solo elige textos** a partir de lo que ya calculó el servidor
 * (`GET /attendance/state` + `GET /attendance/marks?limit=3`): nunca decide el estado IN/OUT, la
 * acción propuesta ni compara horas contra `max_open_shift_hours`. El único cómputo propio es
 * encontrar en qué día operativo cae la entrada abierta (`state.last_mark`) para poder decir
 * "· ayer" en vez de repetir la sede cuando el turno viene de un día anterior (US1-8).
 */
import type { AttendanceStateResponse, StaffAttendanceDay, StaffAttendanceMark } from '@fermin/contracts';

import { dayLabel, formatTime, t } from '../../i18n';

export type JourneyCardKind = 'WORKING' | 'OPEN_SHIFT_EXCEEDED' | 'DONE' | 'NOT_STARTED' | 'UNRESOLVED';

type LastMark = NonNullable<AttendanceStateResponse['last_mark']>;

export interface JourneyEntry {
  type: LastMark['type'];
  serverTimestamp: string;
  siteName: string;
  siteTimezone: string;
  /**
   * "Ayer" o la fecha larga cuando la entrada no es del día operativo `today`; `null` cuando sí lo
   * es (entonces se muestra la sede, no el día — US1-1 vs. US1-8).
   */
  dayLabel: string | null;
}

export interface JourneyCard {
  kind: JourneyCardKind;
  /** Solo con `kind = 'WORKING'`: la entrada abierta, sea de hoy o de un turno nocturno anterior. */
  entry: JourneyEntry | null;
  /** Marcas vigentes del día operativo `today` (pueden estar vacías). */
  todayMarks: readonly StaffAttendanceMark[];
}

export interface JourneyCardInput {
  state: AttendanceStateResponse;
  /** Día operativo actual en la zona de la sede principal (`today` de la respuesta del historial). */
  today: string;
  /** Los días recientes que trajo `GET /attendance/marks?limit=3`. */
  days: readonly StaffAttendanceDay[];
}

/** El día operativo (si lo hay) que contiene exactamente `mark` entre los días recientes. */
function findMarkDay(days: readonly StaffAttendanceDay[], mark: LastMark): StaffAttendanceDay | undefined {
  return days.find((day) =>
    day.marks.some(
      (candidate) =>
        candidate.type === mark.type &&
        candidate.server_timestamp === mark.server_timestamp &&
        candidate.site.id === mark.site.id,
    ),
  );
}

export function journeyCardOf({ state, today, days }: JourneyCardInput): JourneyCard {
  const todayDay = days.find((day) => day.operative_day === today);
  const todayMarks = todayDay?.marks ?? [];

  if (state.state === 'UNRESOLVED' || state.proposed_action === null) {
    return { kind: 'UNRESOLVED', entry: null, todayMarks };
  }

  if (state.state === 'IN' && state.last_mark !== null) {
    const markDay = findMarkDay(days, state.last_mark);
    const label =
      markDay !== undefined && markDay.operative_day !== today
        ? dayLabel(markDay.operative_day, today)
        : null;
    return {
      kind: 'WORKING',
      entry: {
        type: state.last_mark.type,
        serverTimestamp: state.last_mark.server_timestamp,
        siteName: state.last_mark.site.name,
        siteTimezone: state.last_mark.site.timezone,
        dayLabel: label,
      },
      todayMarks,
    };
  }

  if (state.open_shift_exceeded) {
    return { kind: 'OPEN_SHIFT_EXCEEDED', entry: null, todayMarks };
  }

  const lastToday = todayMarks[todayMarks.length - 1];
  if (lastToday !== undefined && lastToday.type === 'CHECK_OUT') {
    return { kind: 'DONE', entry: null, todayMarks };
  }

  return { kind: 'NOT_STARTED', entry: null, todayMarks };
}

/** "Entrada 08:57" / "Salida 18:12", con la hora en la zona de la sede de la marca (R-8). */
export function markLabel(
  mark: Pick<StaffAttendanceMark, 'type' | 'server_timestamp' | 'site_timezone'>,
): string {
  const key = mark.type === 'CHECK_IN' ? 'home.marks.checkIn' : 'home.marks.checkOut';
  return t(key, { time: formatTime(mark.server_timestamp, mark.site_timezone) });
}
